import {
  useEffect, useRef, useState, useCallback 
} from 'react';
import {
  QrCode, X, Keyboard 
} from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/classnames';

interface QRScannerProps {
  readonly onScan: (data: string) => void;
  readonly onCancel?: () => void;
  readonly className?: string;
  readonly expectedPattern?: RegExp;
  readonly placeholder?: string;
}

export function QRScanner({
  onScan,
  onCancel,
  className,
  expectedPattern,
  placeholder = 'Enter PO number manually',
}: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);

  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualValue, setManualValue] = useState('');

  const stopScanning = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsScanning(false);
  }, []);

  const processBarcode = useCallback(async (canvas: HTMLCanvasElement): Promise<string | null> => {
    if (!('BarcodeDetector' in window)) return null;
    
    type BarcodeDetectorType = new (options: { formats: string[] }) => {detect: (source: HTMLCanvasElement) => Promise<Array<{ rawValue: string }>>;};
    const BarcodeDetectorClass = (
      window as unknown as { BarcodeDetector: BarcodeDetectorType }
    ).BarcodeDetector;
    const barcodeDetector = new BarcodeDetectorClass({ formats: ['qr_code'] });
    const barcodes = await barcodeDetector.detect(canvas);
    
    if (barcodes.length === 0) return null;
    
    const data = barcodes[0].rawValue;
    const patternMatches = expectedPattern ? expectedPattern.test(data) : true;
    return patternMatches ? data : null;
  }, [expectedPattern]);

  const scanFrame = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    try {
      const data = await processBarcode(canvas);
      if (data) {
        stopScanning();
        onScan(data);
        return;
      }
    } catch {
      // BarcodeDetector not supported or failed, continue scanning
    }

    animationRef.current = requestAnimationFrame(scanFrame);
  }, [onScan, stopScanning, processBarcode]);

  const startScanning = useCallback(async () => {
    try {
      setError(null);
      stopScanning();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsScanning(true);
        animationRef.current = requestAnimationFrame(scanFrame);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to access camera';
      setError(message);
      setIsScanning(false);
    }
  }, [scanFrame, stopScanning]);

  useEffect(() => {
    if (!showManualEntry) {
      startScanning();
    }
    return stopScanning;
  }, [showManualEntry, startScanning, stopScanning]);

  const handleManualSubmit = useCallback((e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    if (manualValue.trim()) {
      onScan(manualValue.trim());
    }
  }, [manualValue, onScan]);

  if (showManualEntry) {
    return (
      <div className={cn('flex flex-col gap-4 p-6 bg-gray-50 rounded-lg', className)}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Manual Entry</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowManualEntry(false)}
          >
            <QrCode className="w-4 h-4 mr-2" />
            Scan QR
          </Button>
        </div>
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <input
            type="text"
            value={manualValue}
            onChange={(e) => setManualValue(e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
          <Button type="submit" disabled={!manualValue.trim()}>
            Go
          </Button>
        </form>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} className="mt-2">
            Cancel
          </Button>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-4 p-8 bg-gray-100 rounded-lg aspect-video', className)}>
        <QrCode className="w-12 h-12 text-gray-400" />
        <p className="text-sm text-gray-600 text-center">{error}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={startScanning}>
            Try Again
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowManualEntry(true)}>
            <Keyboard className="w-4 h-4 mr-2" />
            Manual Entry
          </Button>
        </div>
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn('relative rounded-lg overflow-hidden bg-black', className)}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full aspect-video object-cover"
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* Scanning overlay */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Corner markers */}
        <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2">
          <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
        </div>

        {/* Scanning line animation */}
        {isScanning && (
          <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 overflow-hidden">
            <div className="absolute w-full h-0.5 bg-green-400 animate-scan" />
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full">
        <p className="text-white text-sm">Point camera at QR code</p>
      </div>

      {/* Controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowManualEntry(true)}
          className="bg-white/90 backdrop-blur-sm"
        >
          <Keyboard className="w-4 h-4 mr-2" />
          Manual Entry
        </Button>
        {onCancel && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        )}
      </div>

      {/* Loading indicator */}
      {!isScanning && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
        </div>
      )}

      <style>{`
        @keyframes scan {
          0% { top: 0; }
          50% { top: 100%; }
          100% { top: 0; }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
